import { FormikHelpers } from 'formik';

import FormSubmitStatus from 'components/FormSubmitStatus';
import { Button } from 'components/Input/Button';
import InputField from 'components/Input/InputField/InputField';

import { useConversation } from 'hooks/conversation';
import { useVendorCommand } from 'hooks/useVendorCommand';
import { PlainTextMessage } from 'routes/Chat/Messages/PlainTextMessage';

import { RatingsType } from '../RatingRequest/types';
import {
  FeedbackRequestDataType,
  FeedbackRequestFormProps,
  FeedbackRequestFormType,
} from './types';

import FormWrapper from 'components/FormWrapper';
import { FORM_IDS } from 'components/FormWrapper/constants';
import { VENDOR_COMMANDS } from 'constants/vendor-commands';
import WithDelayedAnimation from 'routes/Chat/Messages/WithDelayedAnimation/WithDelayedAnimation';
import { htmlId } from 'utils/html';
import { isLastMessage, isMessageRead } from 'utils/twilio';
import { FEEDBACK_RESPONSES, PLACEHOLDER_TEXT } from './constants';
import { ButtonGroup, StyledFeedbackRequestedFormContainer, StyledForm } from './styles';

/**
 * To force trigger in conversation (see Message Templates section in README for more info):
 * message_template_rating_request_form (appears after feedback submission) *
 */
const FeedbackRequestForm = (props: FeedbackRequestFormProps) => {
  const { conversationState } = useConversation();
  const { runVendorCommandAsync } = useVendorCommand();

  const { form, twilioMessageIndex } = props;
  const rating = (form.info as FeedbackRequestDataType).rating.toLocaleLowerCase() as RatingsType;
  const response = FEEDBACK_RESPONSES[rating] ?? FEEDBACK_RESPONSES.average;
  const previouslySubmittedFeedback = (form.info as FeedbackRequestDataType).feedback;
  const isPreviouslyCompleted = form.isCompleted || !!previouslySubmittedFeedback;

  const isLatestMessage = isLastMessage(twilioMessageIndex, conversationState.messages.length);
  const read = isMessageRead(twilioMessageIndex, conversationState);
  const animate = isLatestMessage && !read;

  const onSubmit = async (
    values: FeedbackRequestFormType,
    helpers: FormikHelpers<FeedbackRequestFormType>,
  ) => {
    helpers.setSubmitting(true);
    await runVendorCommandAsync(VENDOR_COMMANDS.CARVANA_FEEDBACK_REQUEST_FORM, {
      form_id: props.form.id,
      twilio_message_id: conversationState.messages[twilioMessageIndex].sid,
      feedback: values.feedback,
      rating,
    });
    helpers.setSubmitting(false);
    helpers.setStatus('submitted');
  };

  const onSubmitError = (error: Error, helpers: FormikHelpers<FeedbackRequestFormType>) => {
    helpers.setSubmitting(false);
    helpers.setStatus(error.message);
  };
  const commonAnimationProps = { animate, showIndicator: true };

  return (
    <StyledFeedbackRequestedFormContainer>
      <WithDelayedAnimation relativeIndex={0} {...commonAnimationProps}>
        <PlainTextMessage text={response} />
      </WithDelayedAnimation>
      <FormWrapper
        id={FORM_IDS.FEEDBACK_REQUEST}
        onSubmit={onSubmit}
        onError={onSubmitError}
        enableReinitialize
        initialValues={{ feedback: previouslySubmittedFeedback }}
      >
        {({ isValid, isValidating, isSubmitting, status, dirty, values }) => {
          const currInput =
            typeof values.feedback === 'string' ? values.feedback.trim() : undefined;
          const inputDisabled = isPreviouslyCompleted || isSubmitting || status === 'submitted';
          const submitDisabled = inputDisabled || !isValid || isValidating || !currInput || !dirty;
          return (
            <StyledForm noValidate>
              <WithDelayedAnimation relativeIndex={1} {...commonAnimationProps}>
                <InputField
                  multiline
                  id='feedback'
                  name='feedback'
                  placeholder={PLACEHOLDER_TEXT}
                  disabled={inputDisabled}
                />
              </WithDelayedAnimation>

              {(isPreviouslyCompleted || status) && (
                <FormSubmitStatus isSuccess={isPreviouslyCompleted || status === 'submitted'} />
              )}
              {!isPreviouslyCompleted && status !== 'submitted' && (
                <ButtonGroup>
                  <Button
                    id={htmlId('btn-feedback-request-form-submit')}
                    type='submit'
                    disabled={submitDisabled}
                  >
                    Submit
                  </Button>
                </ButtonGroup>
              )}
            </StyledForm>
          );
        }}
      </FormWrapper>
    </StyledFeedbackRequestedFormContainer>
  );
};

export default FeedbackRequestForm;
